import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import SummaryCard from "./SummaryCard";
import ConfirmDialog from "./ConfirmDialog";
import Modal from "../../../../shared/Modal";

import logger from '../../../../../utils/logger';

const ESTATE_PLAN_TYPES = [
  "Will",
  "Trust_Revocable",
  "POA_Health",
  "POA_Financial",
  "Advanced_Directive",
  "Life_Insurance_Trust",
  "Family_Limited_Partnership",
  "Other",
];

const DOCUMENT_STATUS = [
  "Draft",
  "Final",
  "Executed",
  "Needs_Review",
  "Expired",
  "Superseded",
];

const TIMEFRAME_OPTIONS = [
  "0-6 months",
  "6-12 months",
  "1-2 years",
  "2+ years",
];

const INTEREST_STATUS = {
  NOT_INTERESTED: "not_interested",
  INTERESTED: "interested",
};

const STATUS_STYLES = {
  Active: {
    backgroundColor: "#43a047",
    color: "white",
    borderRadius: "16px",
    padding: "0 12px",
  },
  "In Progress": {
    backgroundColor: "#fb8c00",
    color: "white",
    borderRadius: "16px",
    padding: "0 12px",
  },
  "Needs Update": {
    backgroundColor: "#e65100",
    color: "white",
    borderRadius: "16px",
    padding: "0 12px",
  },
  Interested: {
    backgroundColor: "#43a047",
    color: "white",
    borderRadius: "16px",
    padding: "0 12px",
  },
  "Not Started": {
    backgroundColor: "#e0e0e0",
    color: "rgba(0, 0, 0, 0.87)",
    borderRadius: "16px",
    padding: "0 12px",
  },
  "Not Interested": {
    backgroundColor: "#d32f2f",
    color: "white",
    borderRadius: "16px",
    padding: "0 12px",
  },
};

// Helper function to map database status to display status
const mapStatusToDisplay = (dbStatus) => {
  switch (dbStatus) {
    case "Executed":
      return "Active";
    case "Needs_Review":
      return "Needs Update";
    case "Draft":
      return "In Progress";
    default:
      return dbStatus;
  }
};

// Helper function to map display status back to database status
const mapDisplayToDbStatus = (displayStatus) => {
  switch (displayStatus) {
    case "Active":
      return "Executed";
    case "Needs Update":
      return "Needs_Review";
    case "In Progress":
      return "Draft";
    default:
      return displayStatus;
  }
};

// Add a helper function for getting chip color based on status
const getStatusChipStyle = (status) => {
  switch (status) {
    case "Active":
      return {
        backgroundColor: "#4caf50",
        color: "#fff",
      };
    case "In Progress":
      return {
        backgroundColor: "#ff9800",
        color: "#fff",
      };
    case "Needs Update":
      return {
        backgroundColor: "#f57c00",
        color: "#fff",
      };
    case "Interested":
      return {
        backgroundColor: "#4caf50",
        color: "#fff",
      };
    case "Not Interested":
      return {
        backgroundColor: "#f44336",
        color: "#fff",
      };
    default:
      return {
        backgroundColor: "#e0e0e0",
        color: "#000",
      };
  }
};

const EstatePlanSection = ({
  data,
  onUpdate,
  memberId,
  householdId,
  hideTitle,
}) => {
  const [activeModal, setActiveModal] = useState({
    type: null, // 'details' or 'interest'
    view: null, // 'list' or 'form'
    data: null,
  });

  const [viewAllModal, setViewAllModal] = useState({
    open: false,
    type: null, // 'documents' or 'beneficiaries'
  });

  const [formData, setFormData] = useState({
    hasEstatePlan: data?.hasEstatePlan || false,
    estatePlanDetails: data?.estatePlanDetails || [],
    futureInterest: {
      status: data?.futureInterest?.status || "",
      preferredTimeframe: data?.futureInterest?.preferredTimeframe || "",
      notes: data?.futureInterest?.notes || "",
    },
  });

  // Add state for form validation
  const [formErrors, setFormErrors] = useState({});

  // Add new state for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
  });

  // Add new state for reset confirmation
  const [resetConfirm, setResetConfirm] = useState({
    open: false,
  });

  // Modal handlers
  const handleModalOpen = (type, view = "list", data = null) => {
    const emptyEstatePlan = {
      type: "",
      documentStatus: "Not Started",
      lastReviewDate: "",
      nextReviewDate: "",
      estateValue: "",
      beneficiaries: [],
      notes: "",
    };

    const emptyInterest = {
      status: "",
      preferredTimeframe: "",
      notes: "",
    };

    setActiveModal({
      type,
      view,
      data:
        view === "form"
          ? data || (type === "details" ? emptyEstatePlan : emptyInterest)
          : data,
    });
  };

  const handleModalClose = () => {
    setActiveModal({ type: null, view: null, data: null });
  };

  // Update handlers
  const handleUpdateEstatePlan = async (details) => {
    if (!details) return;

    const newEstatePlanDetails = [...formData.estatePlanDetails];
    const updatedDetails = {
      ...details,
      documentStatus: details.documentStatus || "Not Started",
    };

    if (activeModal.data && activeModal.data.id) {
      // Editing existing plan
      const index = newEstatePlanDetails.findIndex(
        (p) => p.id === activeModal.data.id,
      );
      if (index !== -1) {
        newEstatePlanDetails[index] = updatedDetails;
      }
    } else {
      // Adding new plan
      newEstatePlanDetails.push(updatedDetails);
    }

    const newData = {
      hasEstatePlan: true,
      estatePlanDetails: newEstatePlanDetails,
      futureInterest: formData.futureInterest,
    };

    try {
      await onUpdate(newData);
      setFormData((prev) => ({
        ...prev,
        hasEstatePlan: true,
        estatePlanDetails: newEstatePlanDetails,
      }));
    } catch (error) {
      logger.error("Error updating estate plan:", error);
    }

    handleModalClose();
  };

  // Update the future interest handler
  const handleUpdateFutureInterest = async (interest) => {
    if (!interest?.status) {
      const resetData = {
        hasEstatePlan: false,
        estatePlanDetails: [],
        futureInterest: {
          status: "",
          preferredTimeframe: "",
          notes: "",
        },
      };
      await onUpdate(resetData);
      setFormData(resetData);
      handleModalClose();
      return;
    }

    if (
      interest.status === INTEREST_STATUS.INTERESTED &&
      !interest.preferredTimeframe
    ) {
      setFormErrors({
        preferredTimeframe: "Please select a preferred timeframe",
      });
      return;
    }

    const newData = {
      hasEstatePlan: false,
      estatePlanDetails: [],
      futureInterest: {
        status: interest.status,
        preferredTimeframe:
          interest.status === INTEREST_STATUS.NOT_INTERESTED
            ? "N/A"
            : interest.preferredTimeframe,
        notes: interest.notes || "",
      },
    };

    try {
      await onUpdate(newData);
      setFormData(newData);
    } catch (error) {
      logger.error("Error updating interest:", error);
    }

    setFormErrors({});
    handleModalClose();
  };

  // Update the estate plan toggle handler
  const handleEstatePlanToggle = async (event) => {
    const hasEstatePlan = event.target.checked;

    const newData = {
      hasEstatePlan,
      estatePlanDetails: hasEstatePlan ? formData.estatePlanDetails : [],
      futureInterest: !hasEstatePlan
        ? formData.futureInterest
        : {
            status: "",
            preferredTimeframe: "",
            notes: "",
          },
    };

    try {
      await onUpdate(newData);
      setFormData(newData);
    } catch (error) {
      logger.error("Error toggling estate plan:", error);
    }
  };

  // Update the status calculation
  const getEstatePlanStatus = () => {
    if (!formData.hasEstatePlan) {
      if (formData.futureInterest?.status === INTEREST_STATUS.NOT_INTERESTED) {
        return "Not_Interested";
      }
      return formData.futureInterest?.preferredTimeframe
        ? "Interested"
        : "Not_Started";
    }

    // Count only documents with 'Active' status
    const activeCount = formData.estatePlanDetails.filter(
      (plan) => mapStatusToDisplay(plan.document_status) === "Active",
    ).length;

    if (activeCount > 0) {
      const style = getStatusChipStyle("Active");
      return (
        <Box sx={{ ml: "auto" }}>
          <Chip
            label={`Active (${activeCount})`}
            size="small"
            sx={{
              bgcolor: style.backgroundColor,
              color: style.color,
              "& .MuiChip-label": {
                textTransform: "capitalize",
              },
            }}
          />
        </Box>
      );
    }
    return "Not_Started";
  };

  // Update the metrics generators
  const getEstatePlanMetrics = () => {
    if (!formData.estatePlanDetails) return [];

    // Count documents by status
    const statusCounts = formData.estatePlanDetails.reduce((acc, plan) => {
      const displayStatus = mapStatusToDisplay(plan.document_status);
      acc[displayStatus] = (acc[displayStatus] || 0) + 1;
      return acc;
    }, {});

    return [
      { label: "Documents", value: formData.estatePlanDetails.length },
      {
        label: "Status",
        value: (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Box
                key={status}
                component="span"
                sx={{
                  display: "inline-block",
                  ...STATUS_STYLES[status],
                  fontSize: "0.75rem",
                  height: "20px",
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                }}
              >
                {`${status} (${count})`}
              </Box>
            ))}
          </Box>
        ),
      },
    ];
  };

  const getFutureInterestMetrics = () => {
    if (!formData.futureInterest) return [];
    return [
      {
        label: "Preferred Timeframe",
        value: formData.futureInterest.preferredTimeframe || "Not Specified",
      },
      { label: "Status", value: data?.status || "Not Started" },
    ];
  };

  // Add function to render estate plan summary
  const renderEstatePlanSummary = () => {
    if (!formData.estatePlanDetails?.length) return null;

    const statusCounts = formData.estatePlanDetails.reduce((acc, plan) => {
      const displayStatus = mapStatusToDisplay(plan.document_status);
      acc[displayStatus] = (acc[displayStatus] || 0) + 1;
      return acc;
    }, {});

    return (
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Estate Documents</Typography>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => setViewAllModal({ open: true, type: "documents" })}
            >
              {formData.estatePlanDetails.length > 1 ? "View All" : "View"}
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Chip
                key={status}
                size="small"
                label={`${status} (${count})`}
                sx={{
                  "& .MuiChip-label": {
                    textTransform: "capitalize",
                  },
                  ...getStatusChipStyle(status),
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Add function to render beneficiaries summary
  const renderBeneficiariesSummary = () => {
    if (!formData.estatePlanDetails?.length) return null;

    return (
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Beneficiaries</Typography>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() =>
                setViewAllModal({ open: true, type: "beneficiaries" })
              }
            >
              {formData.estatePlanDetails.length > 1 ? "View All" : "View"}
            </Button>
          </Box>
          <Typography variant="body2">
            Primary:{" "}
            {formData.estatePlanDetails[0].beneficiaries?.[0] || "None"}
            <br />
            Total: {formData.estatePlanDetails[0].beneficiaries?.length ||
              0}{" "}
            beneficiaries
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // Add View All Modal
  const renderViewAllModal = () => {
    const isDocuments = viewAllModal.type === "documents";
    const items = isDocuments
      ? formData.estatePlanDetails || []
      : formData.estatePlanDetails?.map((plan) => plan.beneficiaries).flat() ||
        [];

    return (
      <Modal
        isOpen={viewAllModal.open}
        onClose={() => setViewAllModal({ open: false, type: null })}
        maxWidth="4xl"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography variant="h6">
              {isDocuments
                ? "Estate Planning Documents"
                : "Estate Plan Beneficiaries"}
            </Typography>
            <button
              onClick={() => setViewAllModal({ open: false, type: null })}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Close
            </button>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                {isDocuments ? (
                  <>
                    <TableCell>Document Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Review</TableCell>
                    <TableCell>Next Review</TableCell>
                    <TableCell>Actions</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Beneficiary</TableCell>
                    <TableCell>Relationship</TableCell>
                    <TableCell>Share</TableCell>
                    <TableCell>Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isDocuments
                ? formData.estatePlanDetails.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>{item.type || item}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ...STATUS_STYLES[
                              mapStatusToDisplay(item.document_status)
                            ],
                            fontSize: "0.75rem",
                            height: "20px",
                            lineHeight: "20px",
                          }}
                        >
                          {mapStatusToDisplay(item.document_status)}
                        </Box>
                      </TableCell>
                      <TableCell>{item.lastReviewDate || "N/A"}</TableCell>
                      <TableCell>{item.nextReviewDate || "N/A"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleModalOpen("details", "form", item);
                              setViewAllModal({ open: false, type: null });
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteConfirm(item.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                : items.map((beneficiary, index) => (
                    <TableRow key={index}>
                      <TableCell>{beneficiary}</TableCell>
                      <TableCell>Primary</TableCell>
                      <TableCell>Equal</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleModalOpen(
                                "details",
                                "form",
                                formData.estatePlanDetails.find((p) =>
                                  p.beneficiaries?.includes(beneficiary),
                                ),
                              );
                              setViewAllModal({ open: false, type: null });
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteConfirm(
                                formData.estatePlanDetails.find((p) =>
                                  p.beneficiaries?.includes(beneficiary),
                                )?.id,
                              )
                            }
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setViewAllModal({ open: false, type: null })}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Update the delete handler
  const handleDeleteEstatePlan = (planId) => {
    const newEstatePlanDetails = formData.estatePlanDetails.filter(
      (p) => p.id !== planId,
    );

    // Check if this was the last plan
    const isLastPlan = newEstatePlanDetails.length === 0;

    const newData = {
      ...data,
      hasEstatePlan: !isLastPlan,
      estatePlanDetails: newEstatePlanDetails,
    };

    // Update both the parent state and local state
    onUpdate(newData);
    setFormData((prev) => ({
      ...prev,
      hasEstatePlan: !isLastPlan,
      estatePlanDetails: newEstatePlanDetails,
    }));

    // Only close modals if we're in the form view
    if (isLastPlan && activeModal.view === "form") {
      handleModalClose();
    }
  };

  // Update the delete confirmation handler
  const handleDeleteConfirm = (id) => {
    setDeleteConfirm({
      open: true,
      id,
    });
  };

  const handleDeleteConfirmed = () => {
    handleDeleteEstatePlan(deleteConfirm.id);
    setDeleteConfirm({ open: false, id: null });
  };

  // Add handler for removing interest
  const handleRemoveInterest = () => {
    const newData = {
      ...data,
      hasEstatePlan: false,
      futureInterest: {
        status: "",
        preferredTimeframe: "",
        notes: "",
      },
    };

    onUpdate(newData);
    setFormData((prev) => ({
      ...prev,
      hasEstatePlan: false,
      futureInterest: newData.futureInterest,
    }));
  };

  // Update the reset handler to only clear the form
  const handleResetConfirmed = () => {
    setResetConfirm({ open: false });
    // Reset only the form data, not the stored data
    setActiveModal((prev) => ({
      ...prev,
      data: {
        status: "",
        preferredTimeframe: "",
        notes: "",
      },
    }));
  };

  return (
    <Box>
      {!hideTitle && (
        <Typography variant="h6" gutterBottom>
          Estate Planning
        </Typography>
      )}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.hasEstatePlan}
              onChange={handleEstatePlanToggle}
            />
          }
          label="I have an estate plan"
          sx={{ ml: 2 }}
        />
      </Box>
      <Grid container spacing={3}>
        {formData.hasEstatePlan ? (
          <Grid item xs={12}>
            <SummaryCard
              title="Estate Plan Details"
              subtitle="Manage your estate planning documents and beneficiaries"
              titleExtra={getEstatePlanStatus()}
              metrics={getEstatePlanMetrics()}
              onAction={() => {
                if (formData.estatePlanDetails.length) {
                  handleModalOpen("details", "list");
                } else {
                  handleModalOpen("details", "form");
                }
              }}
              actionLabel={
                !formData.estatePlanDetails.length
                  ? "Add Plan"
                  : formData.estatePlanDetails.length === 1
                    ? "View/Edit"
                    : "View All/Edit"
              }
              secondaryAction={
                formData.estatePlanDetails.length ? (
                  <Button
                    size="small"
                    onClick={() => {
                      handleModalClose();
                      setTimeout(() => {
                        handleModalOpen("details", "form");
                      }, 100);
                    }}
                    startIcon={<AddIcon />}
                  >
                    Add Plan
                  </Button>
                ) : null
              }
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <SummaryCard
              title="Future Planning Interest"
              subtitle="Express your interest in estate planning"
              titleExtra={getEstatePlanStatus()}
              metrics={getFutureInterestMetrics()}
              onAction={() =>
                handleModalOpen("interest", "form", formData.futureInterest)
              }
              actionLabel={
                formData.futureInterest?.status === INTEREST_STATUS.INTERESTED
                  ? "Edit/Remove Interest"
                  : "Edit Interest"
              }
              secondaryAction={null}
            />
          </Grid>
        )}
      </Grid>
      {/* Estate Plan List View Modal */}
      <Modal
        isOpen={activeModal.type === "details" && activeModal.view === "list"}
        onClose={handleModalClose}
        maxWidth="4xl"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography variant="h6">Estate Planning Documents</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleModalOpen("details", "form")}
              variant="contained"
              color="primary"
            >
              Add New Plan
            </Button>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.estatePlanDetails.map((plan, index) => (
                <TableRow key={plan.id || index}>
                  <TableCell>{plan.document_type.replace(/_/g, " ")}</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        ...STATUS_STYLES[
                          mapStatusToDisplay(plan.document_status)
                        ],
                        fontSize: "0.75rem",
                        height: "20px",
                        lineHeight: "20px",
                      }}
                    >
                      {mapStatusToDisplay(plan.document_status)}
                    </Box>
                  </TableCell>
                  <TableCell>{plan.effective_date || "N/A"}</TableCell>
                  <TableCell>{plan.expiration_date || "N/A"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleModalOpen("details", "form", plan)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteConfirm(plan.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setViewAllModal({ open: false, type: null })}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      {/* Estate Plan Form View Modal */}
      <Modal
        isOpen={activeModal.type === "details" && activeModal.view === "form"}
        onClose={handleModalClose}
        maxWidth="2xl"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6">
            {activeModal.data?.id ? "Edit Estate Plan" : "New Estate Plan"}
          </h2>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={activeModal.data?.document_type || ""}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, document_type: e.target.value },
                    }))
                  }
                  label="Document Type"
                >
                  {ESTATE_PLAN_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={activeModal.data?.document_status || ""}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, document_status: e.target.value },
                    }))
                  }
                  label="Document Status"
                >
                  {DOCUMENT_STATUS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Effective Date"
                value={activeModal.data?.effective_date || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, effective_date: e.target.value },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiration Date"
                value={activeModal.data?.expiration_date || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, expiration_date: e.target.value },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Jurisdiction"
                value={activeModal.data?.jurisdiction || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, jurisdiction: e.target.value },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Attorney Firm"
                value={activeModal.data?.attorney_firm || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, attorney_firm: e.target.value },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Attorney Contact Info"
                multiline
                rows={2}
                value={activeModal.data?.attorney_contact_info || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: {
                      ...prev.data,
                      attorney_contact_info: e.target.value,
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={4}
                value={activeModal.data?.additional_notes || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, additional_notes: e.target.value },
                  }))
                }
              />
            </Grid>
          </Grid>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleModalClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleUpdateEstatePlan(activeModal.data)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
      {/* Future Interest Modal */}
      <Modal
        isOpen={activeModal.type === "interest"}
        onClose={handleModalClose}
        maxWidth="xl"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography variant="h6">Express Interest in Estate Planning</Typography>
            {formData.futureInterest?.status && (
              <IconButton
                onClick={() => handleResetConfirmed()}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                size="small"
                title="Reset Interest"
              >
                <RestartAltIcon />
              </IconButton>
            )}
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.status}>
                <InputLabel>Interest Status</InputLabel>
                <Select
                  value={activeModal.data?.status || ""}
                  onChange={(e) => {
                    setFormErrors({});
                    setActiveModal((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        status: e.target.value,
                        preferredTimeframe:
                          e.target.value === INTEREST_STATUS.NOT_INTERESTED
                            ? "N/A"
                            : prev.data?.preferredTimeframe,
                      },
                    }));
                  }}
                  label="Interest Status"
                >
                  <MenuItem value={INTEREST_STATUS.INTERESTED}>
                    Interested
                  </MenuItem>
                  <MenuItem value={INTEREST_STATUS.NOT_INTERESTED}>
                    Not Interested
                  </MenuItem>
                </Select>
                {formErrors.status && (
                  <FormHelperText>{formErrors.status}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {activeModal.data?.status === INTEREST_STATUS.INTERESTED && (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.preferredTimeframe}>
                  <InputLabel>Preferred Timeframe</InputLabel>
                  <Select
                    value={activeModal.data?.preferredTimeframe || ""}
                    onChange={(e) => {
                      setFormErrors({});
                      setActiveModal((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          preferredTimeframe: e.target.value,
                        },
                      }));
                    }}
                    label="Preferred Timeframe"
                  >
                    {TIMEFRAME_OPTIONS.map((timeframe) => (
                      <MenuItem key={timeframe} value={timeframe}>
                        {timeframe}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.preferredTimeframe && (
                    <FormHelperText>
                      {formErrors.preferredTimeframe}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes and Concerns"
                value={activeModal.data?.notes || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, notes: e.target.value },
                  }))
                }
              />
            </Grid>
          </Grid>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleModalClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!activeModal.data?.status) {
                  handleUpdateFutureInterest(activeModal.data);
                } else {
                  handleUpdateFutureInterest(activeModal.data);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
      {/* View All Modal */}
      {renderViewAllModal()}
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirm Delete"
        message="Are you sure you want to delete this estate plan document?"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
      />
      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={resetConfirm.open}
        title="Confirm Reset"
        message="Are you sure you want to reset interest status? This will clear all interest information."
        onConfirm={handleResetConfirmed}
        onCancel={() => setResetConfirm({ open: false })}
      />
    </Box>
  );
};

export default EstatePlanSection;
